/**
 * MFA SERVICE - Firebase Multi-Factor Authentication
 * 
 * Implementa 2FA real usando Firebase MFA nativo.
 * 
 * FLUJO DE ENROLLMENT (durante onboarding o configuración):
 * 1. Usuario autenticado con email/password
 * 2. Iniciar sesión de enrollment MFA
 * 3. Enviar código SMS
 * 4. Verificar código → Teléfono queda como segundo factor
 * 
 * FLUJO DE LOGIN (en futuros logins):
 * 1. Usuario intenta login con email/password
 * 2. Firebase lanza error 'auth/multi-factor-auth-required'
 * 3. Capturamos el resolver y enviamos SMS
 * 4. Usuario verifica código → Login completo
 * 
 * REQUISITOS FIREBASE:
 * - Identity Platform habilitado
 * - Phone Authentication habilitado
 * 
 * CUMPLIMIENTO: GDPR Art. 32, LOPD - 2FA Real
 */

import { auth } from '@/core/config/firebase';
import { Logger } from '@/core/utils';

// Firebase v8 compat
declare const firebase: any;

const logger = Logger.getLogger('MFAService');

export interface MFAResult {
  success: boolean;
  error?: string;
}

export interface MFALoginResolver {
  resolver: any;
  hints: Array<{
    uid: string;
    phoneNumber: string;
    displayName: string;
  }>;
}

class MFAService {
  private static instance: MFAService;
  private recaptchaVerifier: any = null;
  private verificationId: string | null = null;
  private enrollmentSession: any = null;
  private loginResolver: any = null;

  private constructor() {}

  static getInstance(): MFAService {
    if (!MFAService.instance) {
      MFAService.instance = new MFAService();
    }
    return MFAService.instance;
  }

  /**
   * Inicializa el reCAPTCHA (requerido por Firebase)
   */
  async initializeRecaptcha(containerId: string): Promise<boolean> {
    try {
      // Limpiar verifier anterior
      this.cleanupRecaptcha();

      // Crear nuevo verifier invisible
      this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(containerId, {
        size: 'invisible',
        callback: () => {
          logger.info('reCAPTCHA resuelto');
        },
        'expired-callback': () => {
          logger.warn('reCAPTCHA expirado, se reinicializará automáticamente');
          // No limpiar aquí, lo haremos al reintentar
        }
      });

      await this.recaptchaVerifier.render();
      logger.info('reCAPTCHA inicializado');
      return true;
    } catch (error: any) {
      logger.error('Error inicializando reCAPTCHA', { error: error.message });
      return false;
    }
  }

  /**
   * Limpia el reCAPTCHA de forma segura
   */
  private cleanupRecaptcha(): void {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
      } catch (e) {
        // Ignorar errores de limpieza
      }
      this.recaptchaVerifier = null;
    }
  }

  /**
   * Fuerza la reinicialización del reCAPTCHA
   */
  async refreshRecaptcha(containerId: string): Promise<boolean> {
    logger.info('Refrescando reCAPTCHA');
    return this.initializeRecaptcha(containerId);
  }

  /**
   * Formatea el número de teléfono al formato E.164
   */
  private formatPhoneNumber(phone: string, countryCode: string = '+34'): string {
    let cleaned = phone.replace(/[^\d]/g, '');
    if (phone.startsWith('+')) {
      return phone.replace(/[^\d+]/g, '');
    }
    return `${countryCode}${cleaned}`;
  }

  // ===========================================
  // ENROLLMENT - Añadir teléfono como 2º factor
  // ===========================================

  /**
   * Inicia el proceso de enrollment MFA
   * Debe llamarse cuando el usuario está autenticado con email/password
   * @param phoneNumber - Número de teléfono
   * @param countryCode - Código de país (default: +34)
   * @param containerId - ID del contenedor reCAPTCHA para reinicialización
   */
  async startEnrollment(
    phoneNumber: string, 
    countryCode: string = '+34',
    containerId: string = 'mfa-enrollment-recaptcha'
  ): Promise<MFAResult> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { success: false, error: 'Debes estar autenticado para configurar 2FA' };
    }

    try {
      // Siempre reinicializar reCAPTCHA antes de enviar (evita tokens expirados)
      logger.info('Reinicializando reCAPTCHA antes de enrollment');
      const recaptchaReady = await this.refreshRecaptcha(containerId);
      if (!recaptchaReady) {
        return { success: false, error: 'No se pudo inicializar la verificación de seguridad. Recarga la página.' };
      }

      const formattedPhone = this.formatPhoneNumber(phoneNumber, countryCode);
      logger.info('Iniciando enrollment MFA', { 
        phone: formattedPhone.substring(0, 6) + '****' 
      });

      // Obtener la sesión de enrollment de multifactor
      const multiFactorUser = currentUser.multiFactor;
      this.enrollmentSession = await multiFactorUser.getSession();

      // Crear el proveedor de teléfono con la información
      const phoneInfoOptions = {
        phoneNumber: formattedPhone,
        session: this.enrollmentSession
      };

      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
      this.verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        this.recaptchaVerifier
      );

      logger.info('SMS de enrollment enviado');
      return { success: true };

    } catch (error: any) {
      logger.error('Error iniciando enrollment MFA', { 
        code: error.code, 
        message: error.message 
      });
      
      // Si es error de reCAPTCHA, limpiar para próximo intento
      if (error.code === 'auth/invalid-app-credential') {
        this.cleanupRecaptcha();
      }
      
      return { success: false, error: this.mapError(error) };
    }
  }

  /**
   * Completa el enrollment MFA verificando el código SMS
   */
  async completeEnrollment(code: string, displayName: string = 'Teléfono principal'): Promise<MFAResult> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { success: false, error: 'Sesión expirada' };
    }

    if (!this.verificationId) {
      return { success: false, error: 'No hay verificación pendiente. Solicita un nuevo código.' };
    }

    try {
      logger.info('Completando enrollment MFA');

      // Crear la credencial con el código
      const credential = firebase.auth.PhoneAuthProvider.credential(
        this.verificationId,
        code
      );

      // Crear la aserción de multifactor
      const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(credential);

      // Enrollar el segundo factor
      await currentUser.multiFactor.enroll(multiFactorAssertion, displayName);

      // Limpiar estado
      this.verificationId = null;
      this.enrollmentSession = null;

      logger.info('Enrollment MFA completado exitosamente');
      return { success: true };

    } catch (error: any) {
      logger.error('Error completando enrollment MFA', { 
        code: error.code, 
        message: error.message 
      });
      return { success: false, error: this.mapError(error) };
    }
  }

  // ===========================================
  // LOGIN - Manejar 2FA durante login
  // ===========================================

  /**
   * Procesa el error de MFA requerido durante login
   * Retorna la información necesaria para mostrar al usuario
   */
  handleMFARequired(error: any): MFALoginResolver | null {
    if (error.code !== 'auth/multi-factor-auth-required') {
      return null;
    }

    const resolver = error.resolver;
    if (!resolver) return null;

    this.loginResolver = resolver;

    // Extraer hints de los factores disponibles
    const hints = resolver.hints.map((hint: any) => ({
      uid: hint.uid,
      phoneNumber: hint.phoneNumber,
      displayName: hint.displayName || 'Teléfono'
    }));

    logger.info('MFA requerido durante login', { 
      factorCount: hints.length 
    });

    return { resolver, hints };
  }

  /**
   * Envía SMS para completar el login con MFA
   */
  async sendLoginVerification(hintUid?: string): Promise<MFAResult> {
    if (!this.loginResolver) {
      return { success: false, error: 'No hay proceso de login pendiente' };
    }

    if (!this.recaptchaVerifier) {
      return { success: false, error: 'reCAPTCHA no inicializado' };
    }

    try {
      // Usar el primer hint si no se especifica
      const hint = hintUid 
        ? this.loginResolver.hints.find((h: any) => h.uid === hintUid)
        : this.loginResolver.hints[0];

      if (!hint) {
        return { success: false, error: 'Factor de autenticación no encontrado' };
      }

      logger.info('Enviando SMS de verificación para login', {
        phone: hint.phoneNumber?.substring(0, 6) + '****'
      });

      const phoneInfoOptions = {
        multiFactorHint: hint,
        session: this.loginResolver.session
      };

      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
      this.verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        this.recaptchaVerifier
      );

      logger.info('SMS de login enviado');
      return { success: true };

    } catch (error: any) {
      logger.error('Error enviando SMS de login', { 
        code: error.code, 
        message: error.message 
      });
      return { success: false, error: this.mapError(error) };
    }
  }

  /**
   * Completa el login MFA verificando el código SMS
   */
  async completeLogin(code: string): Promise<MFAResult> {
    if (!this.loginResolver) {
      return { success: false, error: 'No hay proceso de login pendiente' };
    }

    if (!this.verificationId) {
      return { success: false, error: 'No hay verificación pendiente' };
    }

    try {
      logger.info('Completando login MFA');

      // Crear la credencial con el código
      const credential = firebase.auth.PhoneAuthProvider.credential(
        this.verificationId,
        code
      );

      // Crear la aserción de multifactor
      const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(credential);

      // Resolver el login con el segundo factor
      await this.loginResolver.resolveSignIn(multiFactorAssertion);

      // Limpiar estado
      this.verificationId = null;
      this.loginResolver = null;

      logger.info('Login MFA completado exitosamente');
      return { success: true };

    } catch (error: any) {
      logger.error('Error completando login MFA', { 
        code: error.code, 
        message: error.message 
      });
      return { success: false, error: this.mapError(error) };
    }
  }

  // ===========================================
  // UTILIDADES
  // ===========================================

  /**
   * Verifica si el usuario actual tiene MFA habilitado
   */
  hasMFAEnabled(): boolean {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;
    return currentUser.multiFactor?.enrolledFactors?.length > 0;
  }

  /**
   * Obtiene los factores enrollados del usuario actual
   */
  getEnrolledFactors(): Array<{ uid: string; phoneNumber: string; displayName: string }> {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];

    return (currentUser.multiFactor?.enrolledFactors || []).map((factor: any) => ({
      uid: factor.uid,
      phoneNumber: factor.phoneNumber,
      displayName: factor.displayName || 'Teléfono'
    }));
  }

  /**
   * Elimina un factor MFA (requiere reautenticación reciente)
   */
  async unenrollFactor(factorUid: string): Promise<MFAResult> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { success: false, error: 'No autenticado' };
    }

    try {
      const factor = currentUser.multiFactor.enrolledFactors.find(
        (f: any) => f.uid === factorUid
      );

      if (!factor) {
        return { success: false, error: 'Factor no encontrado' };
      }

      await currentUser.multiFactor.unenroll(factor);
      logger.info('Factor MFA eliminado', { factorUid });
      return { success: true };

    } catch (error: any) {
      logger.error('Error eliminando factor MFA', { 
        code: error.code, 
        message: error.message 
      });
      return { success: false, error: this.mapError(error) };
    }
  }

  /**
   * Mapea errores de Firebase a mensajes amigables
   */
  private mapError(error: any): string {
    switch (error.code) {
      case 'auth/invalid-phone-number':
        return 'Número de teléfono inválido. Verifica el formato.';
      case 'auth/too-many-requests':
        return 'Demasiados intentos. Espera unos minutos.';
      case 'auth/captcha-check-failed':
        return 'Error de verificación. Recarga la página.';
      case 'auth/quota-exceeded':
        return 'Límite de SMS alcanzado. Contacta soporte.';
      case 'auth/invalid-verification-code':
        return 'Código incorrecto. Verifica e intenta de nuevo.';
      case 'auth/code-expired':
        return 'El código ha expirado. Solicita uno nuevo.';
      case 'auth/session-expired':
        return 'La sesión ha expirado. Solicita un nuevo código.';
      case 'auth/second-factor-already-in-use':
        return 'Este teléfono ya está registrado como 2FA en otra cuenta.';
      case 'auth/maximum-second-factor-count-exceeded':
        return 'Has alcanzado el límite de factores de autenticación.';
      case 'auth/unsupported-first-factor':
        return 'El proveedor de inicio de sesión actual no soporta MFA.';
      case 'auth/unverified-email':
        return 'Debes verificar tu email antes de configurar 2FA.';
      case 'auth/requires-recent-login':
        return 'Por seguridad, vuelve a iniciar sesión e intenta de nuevo.';
      default:
        return error.message || 'Error desconocido';
    }
  }

  /**
   * Limpia el estado del servicio
   */
  cleanup(): void {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
      } catch (e) {
        // Ignorar
      }
      this.recaptchaVerifier = null;
    }
    this.verificationId = null;
    this.enrollmentSession = null;
    this.loginResolver = null;
    logger.debug('MFAService limpiado');
  }
}

export const mfaService = MFAService.getInstance();

